import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { LeadListItem, CheckStatus } from '@shared/types';
import { listLeads } from '../../api';
import { scoreColor, formatDate } from '../../scoreUtils';

const STATUS_LABEL: Record<CheckStatus, string> = {
  running: '実行中',
  done: '完了',
  error: 'エラー',
};

export default function LeadsPage() {
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLeads(await listLeads());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="loading">読み込み中…</div>;

  return (
    <div className="page">
      <div className="page-head">
        <h1>リード一覧</h1>
        <button className="btn btn--sm btn--ghost" onClick={() => void load()}>
          更新
        </button>
      </div>

      {error && <div className="alert alert--error">{error}</div>}

      {leads.length === 0 ? (
        <p className="muted">まだリードがありません。公開LPからの申込がここに表示されます。</p>
      ) : (
        <div className="card">
          <table className="url-table">
            <thead>
              <tr>
                <th>メールアドレス</th>
                <th>会社名</th>
                <th>電話番号</th>
                <th>診断URL</th>
                <th>最新スコア</th>
                <th>状態</th>
                <th>申込日時</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td>{lead.email}</td>
                  <td>{lead.company ?? <span className="muted small">—</span>}</td>
                  <td>{lead.phone ?? <span className="muted small">—</span>}</td>
                  <td>
                    <div className="url-cell">
                      {lead.latestCheckId ? (
                        <Link
                          className="url-cell__url"
                          to={`/result/${encodeURIComponent(lead.latestCheckId)}`}
                        >
                          {lead.targetUrl}
                        </Link>
                      ) : (
                        <span className="url-cell__url">{lead.targetUrl}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {lead.latestScore != null ? (
                      <span
                        className="score-pill"
                        style={{ backgroundColor: scoreColor(lead.latestScore) }}
                      >
                        {lead.latestScore}
                      </span>
                    ) : (
                      <span className="muted small">未実施</span>
                    )}
                  </td>
                  <td>
                    {lead.latestStatus ? (
                      STATUS_LABEL[lead.latestStatus]
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                  <td className="muted small">{formatDate(lead.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
